// src/pages/OnboardingStatus.tsx: holding portal for allocator, service provider
// and candidate applicants.
//
// Simpler than the manager portal: no regulatory verification and no contract
// stage, so the timeline is three steps rather than five.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { C, fmtDate } from '../lib/applications';
import {
  APPLICATION_TABLES, ApplicantKind,
  AllocatorApplication, ProviderApplication, CandidateApplication,
  organisationTypeLabel, aumRangeLabel, allocationLabel, ticketSizeLabel,
  qualificationLabel, assetClassLabels, clientTypeLabels, pricingModelLabel,
  seniorityLevelLabel,
} from '../lib/onboardingApplications';
import { AltvenoMark } from '../components/ApplyShell';

type Kind = Exclude<ApplicantKind, 'manager'>;

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const STAGES = ['Application Submitted', 'Under Review', 'Account Activated'];

const COPY: Record<Kind, { title: string; badge?: string; note?: string }> = {
  allocator: {
    title: 'Your allocator application',
    badge: 'Free Access',
  },
  provider: {
    title: 'Your service provider application',
    note: 'Our team will be in touch about pricing and next steps.',
  },
  candidate: {
    title: 'Your candidate application',
    badge: 'Free Access',
  },
};

export default function OnboardingStatus({ kind }: { kind: Kind }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [app, setApp] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // RLS scopes this to the signed-in applicant's own rows.
      const { data } = await supabase.from(APPLICATION_TABLES[kind])
        .select('*').order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (cancelled) return;
      setApp(data || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const status = app?.status || 'submitted';
  const copy = COPY[kind];

  const stageState = (i: number): 'done' | 'current' | 'todo' => {
    if (status === 'approved') return 'done';
    if (status === 'declined') return i === 0 ? 'done' : 'todo';
    if (i === 0) return 'done';
    if (i === 1) return 'current';
    return 'todo';
  };

  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ background: C.navy, padding: '18px 32px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <AltvenoMark />
            <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>Altveno</span>
          </div>
          <button onClick={signOut} style={{
            background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '3px',
            color: 'rgba(255,255,255,0.75)', fontSize: '12px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui',
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 24px 64px' }}>
        {loading ? (
          <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading your application…</div>
        ) : !app ? (
          <div style={{ ...card, padding: '44px', textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 10px' }}>No application found</h1>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, margin: '0 0 22px' }}>
              We could not find an application linked to this account.
            </p>
            <button onClick={() => navigate('/apply')} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' }}>
              Start an application
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.text, fontWeight: 400, margin: 0, letterSpacing: '-0.3px' }}>
                {copy.title}
              </h1>
              {copy.badge && (
                <span style={{
                  background: 'rgba(201,168,76,0.14)', color: C.amber,
                  border: `1px solid ${C.gold}66`, borderRadius: '2px',
                  fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.8px',
                  textTransform: 'uppercase', padding: '3px 9px',
                }}>{copy.badge}</span>
              )}
            </div>
            <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 20px' }}>{subjectName(kind, app)}</p>

            {/* ── Status message ── */}
            {status === 'approved' ? (
              <div style={{ ...card, padding: '20px 24px', marginBottom: '16px', borderLeft: `3px solid ${C.success}` }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: C.success, marginBottom: '6px' }}>
                  Your account has been activated
                </div>
                <p style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.7, margin: '0 0 16px' }}>
                  Welcome to Altveno. Everything is ready for you on the platform.
                </p>
                <button onClick={() => navigate('/dashboard')} style={{
                  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
                  padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                }}>Go to the platform</button>
              </div>
            ) : status === 'declined' ? (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '13px', color: C.danger, lineHeight: 1.6 }}>
                Thank you for your interest. Unfortunately we are unable to approve your application at
                this time. Contact hello@altveno.com for more information.
              </div>
            ) : (
              <div style={{ ...card, padding: '13px 16px', marginBottom: '16px', fontSize: '13px', color: C.textSec, lineHeight: 1.6, borderLeft: `3px solid ${C.gold}` }}>
                Your application is under review. We will be in touch within 2 business days.
                {copy.note ? ` ${copy.note}` : ''}
              </div>
            )}

            {app.admin_notes && status !== 'approved' && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '12.5px', color: C.amber, lineHeight: 1.6 }}>
                {app.admin_notes}
              </div>
            )}

            {/* ── Timeline ── */}
            <div style={{ ...card, padding: '24px 26px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '18px' }}>Progress</div>
              {STAGES.map((label, i) => {
                const state = stageState(i);
                const colour = state === 'done' ? C.success : state === 'current' ? C.gold : C.border;
                return (
                  <div key={label} style={{ display: 'flex', gap: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                        background: state === 'todo' ? C.white : colour,
                        border: `1px solid ${colour}`, color: C.white,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: 700,
                      }}>{state === 'done' ? '✓' : state === 'current' ? '•' : ''}</div>
                      {i < STAGES.length - 1 && (
                        <div style={{ width: '1px', flex: 1, minHeight: '26px', background: state === 'done' ? C.success : C.border }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: i < STAGES.length - 1 ? '16px' : 0 }}>
                      <div style={{
                        fontSize: '13.5px', marginTop: '2px',
                        fontWeight: state === 'current' ? 700 : 500,
                        color: state === 'todo' ? C.mutedLt : state === 'current' ? C.navy : C.text,
                      }}>{label}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Summary ── */}
            <div style={{ ...card, padding: '24px 26px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '14px' }}>What you submitted</div>
              {summaryRows(kind, app).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: '12px', padding: '7px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
                  <div style={{ width: '175px', flexShrink: 0, color: C.muted }}>{k}</div>
                  <div style={{ color: C.text, fontWeight: 500, wordBreak: 'break-word' }}>{v || '-'}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '12px', padding: '7px 0', fontSize: '12.5px' }}>
                <div style={{ width: '175px', flexShrink: 0, color: C.muted }}>Submitted</div>
                <div style={{ color: C.text, fontWeight: 500 }}>{fmtDate(app.created_at)}</div>
              </div>
            </div>

            {/* ── Contact ── */}
            <div style={{ ...card, padding: '20px 24px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>Questions about your application?</div>
              <a href="mailto:hello@altveno.com" style={{ fontSize: '13px', color: C.teal, textDecoration: 'none' }}>hello@altveno.com</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function subjectName(kind: Kind, app: any): string {
  if (kind === 'allocator') return (app as AllocatorApplication).organisation_name;
  if (kind === 'provider') return (app as ProviderApplication).company_name;
  return (app as CandidateApplication).contact_name;
}

function summaryRows(kind: Kind, app: any): [string, string][] {
  if (kind === 'allocator') {
    const a = app as AllocatorApplication;
    return [
      ['Organisation', a.organisation_name],
      ['Type', organisationTypeLabel(a.organisation_type)],
      ['Headquarters', [a.headquarters_city, a.headquarters_country].filter(Boolean).join(', ')],
      ['Total AUM', aumRangeLabel(a.total_aum_range)],
      ['Alternatives allocation', allocationLabel(a.alternatives_allocation_pct)],
      ['Asset classes', assetClassLabels(a.asset_classes_of_interest)],
      ['Typical ticket size', ticketSizeLabel(a.typical_ticket_size)],
      ['Qualification', qualificationLabel(a.qualification_basis)],
    ];
  }
  if (kind === 'provider') {
    const p = app as ProviderApplication;
    return [
      ['Company', p.company_name],
      ['Category', p.category],
      ['Headquarters', [p.headquarters_city, p.headquarters_country].filter(Boolean).join(', ')],
      ['Website', p.company_website || ''],
      ['Founded', p.founded_year ? String(p.founded_year) : ''],
      ['Clients', p.client_count != null ? String(p.client_count) : ''],
      ['Regions covered', (p.regions_covered || []).join(', ')],
      ['Target clients', clientTypeLabels(p.target_client_types)],
      ['Pricing model', pricingModelLabel(p.pricing_model)],
    ];
  }
  const c = app as CandidateApplication;
  return [
    ['Current role', c.current_role || ''],
    ['Employer', c.current_employer ? `${c.current_employer}${c.hide_employer ? ' (hidden on profile)' : ''}` : ''],
    ['Years of experience', c.years_experience != null ? String(c.years_experience) : ''],
    ['Seniority', seniorityLevelLabel(c.seniority)],
    ['Target roles', (c.target_roles || []).join(', ')],
    ['Asset classes', assetClassLabels(c.asset_classes)],
    ['Skills', (c.skills || []).join(', ')],
    ['Relocation', c.open_to_relocation ? ((c.relocation_countries || []).join(', ') || 'Yes') : 'No'],
    ['CV', c.cv_url ? 'Uploaded' : 'Not provided'],
  ];
}
